import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { create } from "zustand";
import { resolveItem, useSpaceOf } from "@/data/store";
import { useFolderMeta } from "@/data/sharedFolders";
import { useAuth } from "@/data/auth";
import { diffTexts } from "@/lib/diff";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import s from "./HistoryDialog.module.css";

interface HistoryDialogStore {
  noteId: string | null;
  open: (noteId: string) => void;
  close: () => void;
}
export const useHistoryDialog = create<HistoryDialogStore>((set) => ({
  noteId: null,
  open: (noteId) => set({ noteId }),
  close: () => set({ noteId: null }),
}));

interface VersionMeta {
  id: number;
  createdAt: number;
}

export function HistoryDialog() {
  const { noteId, close } = useHistoryDialog();
  const signedIn = useAuth((a) => a.status === "signedIn");
  const space = useSpaceOf(noteId);
  const folderMeta = useFolderMeta(space?.kind === "shared" ? space.spaceId : null);
  // rolling back rewrites content for every member: owner-only in shared folders
  const mayRestore = space?.kind !== "shared" || folderMeta?.role === "owner";
  const spaceQuery = space?.kind === "shared" ? `?spaceId=${encodeURIComponent(space.spaceId)}` : "";
  const [versions, setVersions] = useState<VersionMeta[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState("");
  const [showDiff, setShowDiff] = useState(true);

  useEffect(() => {
    if (!noteId) return;
    const res = resolveItem(noteId);
    if (res) void res.ctx.notes.readMarkdown(noteId).then(setCurrent);
  }, [noteId]);

  const diff = useMemo(() => {
    if (!showDiff || !preview) return null;
    return diffTexts(preview, current);
  }, [showDiff, preview, current]);

  useEffect(() => {
    if (!noteId || !signedIn) return;
    setLoading(true);
    setVersions([]);
    setSelected(null);
    setPreview("");
    void fetch(`/api/notes/${noteId}/versions${spaceQuery}`)
      .then((r) => r.json())
      .then((data: { versions: VersionMeta[] }) => {
        setVersions(data.versions ?? []);
        if (data.versions?.[0]) setSelected(data.versions[0].id);
      })
      .finally(() => setLoading(false));
  }, [noteId, signedIn, spaceQuery]);

  useEffect(() => {
    if (!noteId || selected === null) return;
    void fetch(`/api/notes/${noteId}/versions/${selected}${spaceQuery}`)
      .then((r) => r.json())
      .then((data: { markdown: string }) => setPreview(data.markdown ?? ""));
  }, [noteId, selected, spaceQuery]);

  const restore = async () => {
    if (!noteId || selected === null) return;
    const res = resolveItem(noteId);
    if (!res) return;
    const handle = res.ctx.notes.open(noteId);
    await handle.whenLoaded;
    // one normal (undoable, syncable) edit, never a doc rebuild
    handle.doc.transact(() => {
      handle.text.delete(0, handle.text.length);
      handle.text.insert(0, preview);
    });
    handle.release();
    res.ctx.workspace.touch(noteId);
    toast.success("Version restored. Undo with Ctrl+Z if needed.");
    close();
  };

  return (
    <Dialog.Root open={noteId !== null} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <Dialog.Title className={s.title}>Version history</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close">
                <X size={14} />
              </IconButton>
            </Dialog.Close>
          </div>
          {!signedIn ? (
            <div className={s.empty}>
              Version history is part of cloud sync.
              <br />
              Sign in and Hydra Notes keeps timestamped snapshots of every note automatically.
            </div>
          ) : loading ? (
            <div className={s.empty}>Loading…</div>
          ) : versions.length === 0 ? (
            <div className={s.empty}>
              No snapshots yet. Versions are captured automatically as you edit
              (about one every 10 minutes of activity).
            </div>
          ) : (
            <>
              <div className={s.body}>
                <div className={s.list}>
                  {versions.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={[s.version, v.id === selected ? s.versionActive : ""].join(" ")}
                      onClick={() => setSelected(v.id)}
                    >
                      {formatWhen(v.createdAt)}
                    </button>
                  ))}
                </div>
                <div className={s.preview}>
                  <pre className={s.previewText}>
                    {showDiff && diff ? (
                      diff.map((p, i) =>
                        p.type === "same" ? (
                          <span key={i}>{p.text}</span>
                        ) : p.type === "added" ? (
                          <ins key={i} className={s.diffAdded} title="Added since this version">
                            {p.text}
                          </ins>
                        ) : (
                          <del key={i} className={s.diffRemoved} title="Present in this version, gone now">
                            {p.text}
                          </del>
                        ),
                      )
                    ) : (
                      preview || " "
                    )}
                  </pre>
                </div>
              </div>
              <div className={s.actions}>
                <span className={s.diffHint}>
                  {showDiff && diff
                    ? "Changes between this version and the note now"
                    : showDiff
                      ? "Note too large to diff, showing content"
                      : "Version content"}
                </span>
                <Button variant="subtle" size="sm" onClick={() => setShowDiff(!showDiff)}>
                  {showDiff ? "Show content" : "Show changes"}
                </Button>
                <Button variant="ghost" onClick={close}>
                  Cancel
                </Button>
                {mayRestore ? (
                  <Button variant="solid" onClick={() => void restore()} disabled={selected === null}>
                    Restore this version
                  </Button>
                ) : (
                  <span className={s.diffHint}>Only the folder owner can restore versions</span>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function formatWhen(ts: number): string {
  const d = new Date(ts);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? `Today ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
